import { Component, Inject, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import Swal from "sweetalert2";

import { Salon } from "../../../../shared/models/salon.model";
import { API_ENDPOINTS } from "../../../../shared/services/api/api-endpoints";
import { ApiService } from "../../../../shared/services/api/api.service";
import { LoaderComponent } from "../../../../shared/components/loader/loader.component";
import { environment } from "../../../../../environments/environment";

type ServiceAddTab = "overview" | "notes";

@Component({
  selector: "business-service-create",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LoaderComponent],
  templateUrl: "./create.component.html",
  styleUrl: "./create.component.scss",
})
export class BusinessServiceCreateComponent implements OnInit {
  assetUrl = environment.assetBaseUrl;

  serviceForm: FormGroup;
  salons: Salon[] = [];

  loading = false;
  isEditMode = false;
  serviceId: string | null = null;

  activeTab: ServiceAddTab = "overview";

  tabs: { key: ServiceAddTab; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "notes", label: "Notes" },
  ];

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private dialogRef: MatDialogRef<BusinessServiceCreateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.serviceForm = this.fb.group({
      salon_id: ["", Validators.required],
      name: ["", [Validators.required, Validators.minLength(2)]],
      price: ["", [Validators.required, Validators.min(0.01)]],
      duration_minutes: [30, [Validators.required, Validators.min(5)]],
      description: [""],
    });

    this.serviceId = data?.service_id || null;
    this.isEditMode = !!this.serviceId;

    if (this.isEditMode) {
      this.updateServiceForm(data);
    }
  }

  ngOnInit(): void {
    this.loadSalons();
  }

  loadSalons(): void {
    this.loading = true;

    this.apiService.request(API_ENDPOINTS.salons.list, "GET", null, "").subscribe({
      next: (response: any) => {
        this.salons = response.results || [];
        this.loading = false;
      },
      error: (error) => {
        console.error("Error loading salons:", error);
        this.loading = false;
      },
    });
  }

  updateServiceForm(data: any): void {
    this.serviceForm.patchValue({
      salon_id: data.salon_id || data.salon || "",
      name: data.name || "",
      price: data.price || "",
      duration_minutes: data.duration_minutes || 30,
      description: data.description || data.notes || "",
    });
  }

  onSubmit(): void {
    if (this.serviceForm.invalid) {
      this.serviceForm.markAllAsTouched();
      this.activeTab = "overview";
      return;
    }

    const formData = this.serviceForm.value;

    this.loading = true;

    if (this.isEditMode && this.serviceId) {
      this.updateService(formData);
    } else {
      this.createService(formData);
    }
  }

  private createService(formData: any): void {
    const payload = {
      name: formData.name,
      price: Number.parseFloat(formData.price),
      duration_minutes: Number(formData.duration_minutes),
      description: formData.description || "",
    };

    this.apiService
      .request(
        API_ENDPOINTS.salons.services.create(formData.salon_id),
        "POST",
        payload,
        "Service Created Successfully!",
      )
      .subscribe({
        next: () => {
          this.loading = false;
          this.dialogRef.close(true);

          Swal.fire({
            title: "Service Created Successfully!",
            icon: "success",
            timer: 5000,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
          });
        },
        error: () => {
          this.loading = false;

          Swal.fire({
            title: "Failed to Create Service",
            text: "Error while creating service. Please try again.",
            icon: "error",
            timer: 5000,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
          });
        },
      });
  }

  private updateService(formData: any): void {
    const payload = {
      services: [
        {
          name: formData.name,
          price: Number.parseFloat(formData.price),
          duration_minutes: Number(formData.duration_minutes),
          description: formData.description || "",
        },
      ],
    };

    this.apiService
      .request(
        API_ENDPOINTS.salons.services.servicesBulkUpsert(formData.salon_id),
        "PUT",
        payload,
        "Service Updated Successfully!",
      )
      .subscribe({
        next: () => {
          this.loading = false;
          this.dialogRef.close(true);

          Swal.fire({
            title: "Service Updated Successfully!",
            icon: "success",
            timer: 5000,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
          });
        },
        error: () => {
          this.loading = false;

          Swal.fire({
            title: "Failed to Update Service",
            text: "Error while updating service. Please try again.",
            icon: "error",
            timer: 5000,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
          });
        },
      });
  }

  checkValidField(fieldName: string): boolean {
    const field = this.serviceForm.get(fieldName);

    if (!field) return true;

    return !(field.invalid && field.touched);
  }

  get selectedSalonName(): string {
    const salonId = String(this.serviceForm.get("salon_id")?.value || "");

    return (
      this.salons.find((salon: any) => String(salon.salon_id) === salonId)
        ?.name || ""
    );
  }

  toMoney(value: any): string {
    const amount = Number(value);

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Number.isFinite(amount) ? amount : 0);
  }

  get modalTitle(): string {
    return this.isEditMode ? "Edit Service" : "Add Service";
  }

  onClose(): void {
    this.dialogRef.close(false);
  }
}